/**
 * AxLabs Mathematics Experiments - Geometry
 *
 * Collection of experiments exploring geometric concepts and relationships.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState } from './helpers.js';
// ============================================================================
// TRIANGLE INEQUALITY INVESTIGATION
// ============================================================================
export const TriangleInequalityInvestigation = createExperimentFromSchema({
    id: 'triangle-inequality-investigation',
    name: 'Triangle Inequality Investigation',
    domain: 'mathematics',
    description: 'Explore the triangle inequality theorem and triangle formation',
    conceptTags: ['Triangle Inequality', 'Triangle Formation', 'Side Lengths', 'Geometric Constraints'],
    difficulty: 2,
    stateVariables: [
        { id: 'a', name: 'Side a', unit: 'dimensionless', initial: 3, symbol: 'a' },
        { id: 'b', name: 'Side b', unit: 'dimensionless', initial: 4, symbol: 'b' },
        { id: 'c', name: 'Side c', unit: 'dimensionless', initial: 5, symbol: 'c' },
        { id: 'sumAB', name: 'a + b', unit: 'dimensionless', initial: 7, symbol: 'a+b' },
        { id: 'sumBC', name: 'b + c', unit: 'dimensionless', initial: 9, symbol: 'b+c' },
        { id: 'sumAC', name: 'a + c', unit: 'dimensionless', initial: 8, symbol: 'a+c' },
        { id: 'isValid', name: 'Valid Triangle', unit: 'dimensionless', initial: 1, symbol: 'V' },
    ],
    parameters: [
        { id: 'a_target', name: 'Target a', unit: 'dimensionless', min: 1, max: 10, default: 3, symbol: 'a' },
        { id: 'b_target', name: 'Target b', unit: 'dimensionless', min: 1, max: 10, default: 4, symbol: 'b' },
        { id: 'c_target', name: 'Target c', unit: 'dimensionless', min: 1, max: 10, default: 5, symbol: 'c' },
        { id: 'rate', name: 'Change Rate', unit: 'dimensionless/s', min: -1, max: 1, default: 0.1, symbol: 'r' },
    ],
    equations: [
        {
            id: 'triangle-inequality',
            name: 'Triangle Inequality Theorem',
            latex: 'a + b > c, \\quad b + c > a, \\quad a + c > b',
            description: 'Sum of any two sides must be greater than the third',
        },
        {
            id: 'degenerate',
            name: 'Degenerate Case',
            latex: 'a + b = c \\implies \\text{collinear points}',
            description: 'When equality holds, points are collinear',
        },
    ],
    derivatives: (state, params) => {
        const rate = getParam(params, 'rate');
        const a = getState(state, 'a');
        const b = getState(state, 'b');
        const c = getState(state, 'c');
        const nextA = Math.max(1, Math.min(10, a + rate * 0.016));
        const nextB = Math.max(1, Math.min(10, b + rate * 0.02));
        const nextC = Math.max(1, Math.min(10, c));
        const sumAB = nextA + nextB;
        const sumBC = nextB + nextC;
        const sumAC = nextA + nextC;
        const isValid = (sumAB > nextC && sumBC > nextA && sumAC > nextB) ? 1 : 0;
        return {
            a: nextA - a,
            b: nextB - b,
            c: nextC - c,
            sumAB: sumAB - getState(state, 'sumAB'),
            sumBC: sumBC - getState(state, 'sumBC'),
            sumAC: sumAC - getState(state, 'sumAC'),
            isValid: isValid - getState(state, 'isValid'),
        };
    },
    bounds: {
        a: { min: 1, max: 10 },
        b: { min: 1, max: 10 },
        c: { min: 1, max: 10 },
    },
});
// ============================================================================
// CIRCLE CHORD PROPERTIES
// ============================================================================
export const CircleChordProperties = createExperimentFromSchema({
    id: 'circle-chord-properties',
    name: 'Circle Chord Properties',
    domain: 'mathematics',
    description: 'Explore properties of chords, arcs, and angles in circles',
    conceptTags: ['Chords', 'Circles', 'Arc Length', 'Central Angle', 'Inscribed Angle'],
    difficulty: 4,
    stateVariables: [
        { id: 'angle', name: 'Central Angle', unit: 'rad', initial: 1, symbol: '\\theta' },
        { id: 'arcLength', name: 'Arc Length', unit: 'dimensionless', initial: 5, symbol: 's' },
        { id: 'chordLength', name: 'Chord Length', unit: 'dimensionless', initial: 4.8, symbol: 'c' },
        { id: 'segmentArea', name: 'Segment Area', unit: 'dimensionless', initial: 1.2, symbol: 'A_{seg}' },
        { id: 'sectorArea', name: 'Sector Area', unit: 'dimensionless', initial: 12.5, symbol: 'A_{sec}' },
        { id: 'inscribedAngle', name: 'Inscribed Angle', unit: 'rad', initial: 0.5, symbol: '\\alpha' },
    ],
    parameters: [
        { id: 'radius', name: 'Radius', unit: 'dimensionless', min: 1, max: 10, default: 5, symbol: 'r' },
        { id: 'angleRate', name: 'Angular Rate', unit: 'rad/s', min: -1, max: 1, default: 0.2, symbol: '\\omega' },
    ],
    equations: [
        {
            id: 'arc-length',
            name: 'Arc Length Formula',
            latex: 's = r\\theta',
            description: 'Arc length equals radius times central angle (in radians)',
        },
        {
            id: 'chord-length',
            name: 'Chord Length Formula',
            latex: 'c = 2r\\sin\\left(\\frac{\\theta}{2}\\right)',
            description: 'Length of chord subtended by central angle',
        },
        {
            id: 'sector-area',
            name: 'Sector Area',
            latex: 'A_{sec} = \\frac{1}{2}r^2\\theta',
            description: 'Area of sector with central angle θ',
        },
        {
            id: 'inscribed-angle',
            name: 'Inscribed Angle Theorem',
            latex: '\\alpha = \\frac{\\theta}{2}',
            description: 'Inscribed angle is half the central angle',
        },
    ],
    derivatives: (state, params) => {
        const r = getParam(params, 'radius');
        const angleRate = getParam(params, 'angleRate');
        const angle = getState(state, 'angle');
        const nextAngle = angle + angleRate * 0.016;
        const arcLength = r * nextAngle;
        const chordLength = 2 * r * Math.sin(nextAngle / 2);
        const sectorArea = 0.5 * r * r * nextAngle;
        const segmentArea = sectorArea - 0.5 * r * r * Math.sin(nextAngle);
        const inscribedAngle = nextAngle / 2;
        return {
            angle: angleRate * 0.016,
            arcLength: arcLength - getState(state, 'arcLength'),
            chordLength: chordLength - getState(state, 'chordLength'),
            segmentArea: segmentArea - getState(state, 'segmentArea'),
            sectorArea: sectorArea - getState(state, 'sectorArea'),
            inscribedAngle: inscribedAngle - getState(state, 'inscribedAngle'),
        };
    },
    bounds: {
        angle: { min: 0, max: Math.PI * 2 },
        arcLength: { min: 0, max: 100 },
        chordLength: { min: 0, max: 20 },
    },
});
// ============================================================================
// ANGLE SUM VERIFICATION
// ============================================================================
export const AngleSumVerification = createExperimentFromSchema({
    id: 'angle-sum-verification',
    name: 'Angle Sum Verification',
    domain: 'mathematics',
    description: 'Verify angle sum properties in polygons and triangles',
    conceptTags: ['Angle Sum', 'Triangles', 'Polygons', 'Interior Angles', 'Exterior Angles'],
    difficulty: 2,
    stateVariables: [
        { id: 'angleA', name: 'Angle A', unit: 'rad', initial: 1.05, symbol: 'A' },
        { id: 'angleB', name: 'Angle B', unit: 'rad', initial: 1.05, symbol: 'B' },
        { id: 'angleC', name: 'Angle C', unit: 'rad', initial: 1.05, symbol: 'C' },
        { id: 'sum', name: 'Sum A+B+C', unit: 'rad', initial: 3.14, symbol: 'A+B+C' },
        { id: 'sumDegrees', name: 'Sum (degrees)', unit: 'deg', initial: 180, symbol: 'A+B+C' },
        { id: 'exteriorSum', name: 'Exterior Sum', unit: 'rad', initial: 6.28, symbol: '\\sum ext' },
    ],
    parameters: [
        { id: 'n', name: 'Polygon Sides', unit: 'dimensionless', min: 3, max: 12, default: 3, symbol: 'n' },
        { id: 'variation', name: 'Variation', unit: 'dimensionless', min: 0, max: 1, default: 0.5, symbol: 'v' },
        { id: 'rate', name: 'Change Rate', unit: 'dimensionless/s', min: 0.1, max: 1, default: 0.3, symbol: 'r' },
    ],
    equations: [
        {
            id: 'triangle-sum',
            name: 'Triangle Angle Sum',
            latex: 'A + B + C = \\pi \\text{ radians} = 180°',
            description: 'Sum of angles in any triangle is 180°',
        },
        {
            id: 'polygon-sum',
            name: 'Polygon Angle Sum',
            latex: 'S = (n-2)\\pi',
            description: 'Sum of interior angles of n-sided polygon',
        },
        {
            id: 'exterior-sum',
            name: 'Exterior Angle Sum',
            latex: '\\sum_{i=1}^n \\text{ext}_i = 2\\pi',
            description: 'Sum of exterior angles of any convex polygon is 360°',
        },
    ],
    derivatives: (state, params) => {
        const n = Math.round(getParam(params, 'n'));
        const variation = getParam(params, 'variation');
        const rate = getParam(params, 'rate');
        const t = getState(state, 'angleA') * rate;
        const interiorSum = (n - 2) * Math.PI;
        const baseAngle = interiorSum / n;
        const angleA = baseAngle + variation * Math.sin(t);
        const angleB = n === 3 ? baseAngle + variation * Math.sin(t + 2 * Math.PI / 3) : baseAngle;
        const angleC = n === 3 ? baseAngle + variation * Math.sin(t + 4 * Math.PI / 3) : baseAngle;
        const sum = angleA + angleB + angleC;
        const sumDegrees = sum * 180 / Math.PI;
        const exteriorSum = 2 * Math.PI;
        return {
            angleA: angleA - getState(state, 'angleA'),
            angleB: angleB - getState(state, 'angleB'),
            angleC: angleC - getState(state, 'angleC'),
            sum: sum - getState(state, 'sum'),
            sumDegrees: sumDegrees - getState(state, 'sumDegrees'),
            exteriorSum: exteriorSum - getState(state, 'exteriorSum'),
        };
    },
    bounds: {
        angleA: { min: 0, max: Math.PI },
        angleB: { min: 0, max: Math.PI },
        angleC: { min: 0, max: Math.PI },
        sum: { min: 0, max: Math.PI * 4 },
    },
});
// ============================================================================
// SIMILARITY RATIO EFFECTS
// ============================================================================
export const SimilarityRatioEffects = createExperimentFromSchema({
    id: 'similarity-ratio-effects',
    name: 'Similarity Ratio Effects',
    domain: 'mathematics',
    description: 'Explore similarity ratios and scale factor effects',
    conceptTags: ['Similarity', 'Scale Factor', 'Area Ratio', 'Volume Ratio', 'Proportions'],
    difficulty: 3,
    stateVariables: [
        { id: 'scale', name: 'Scale Factor k', unit: 'dimensionless', initial: 1, symbol: 'k' },
        { id: 'lengthRatio', name: 'Length Ratio', unit: 'dimensionless', initial: 1, symbol: 'L_2/L_1' },
        { id: 'areaRatio', name: 'Area Ratio', unit: 'dimensionless', initial: 1, symbol: 'A_2/A_1' },
        { id: 'volumeRatio', name: 'Volume Ratio', unit: 'dimensionless', initial: 1, symbol: 'V_2/V_1' },
        { id: 'perimeter1', name: 'Perimeter 1', unit: 'dimensionless', initial: 12, symbol: 'P_1' },
        { id: 'perimeter2', name: 'Perimeter 2', unit: 'dimensionless', initial: 12, symbol: 'P_2' },
    ],
    parameters: [
        { id: 'k_target', name: 'Target Scale', unit: 'dimensionless', min: 0.5, max: 3, default: 1, symbol: 'k' },
        { id: 'rate', name: 'Rate of Change', unit: 'dimensionless/s', min: -0.5, max: 0.5, default: 0.1, symbol: 'r' },
        { id: 'shape', name: 'Shape', unit: 'dimensionless', min: 0, max: 2, default: 0, symbol: 's' },
    ],
    equations: [
        {
            id: 'similarity-length',
            name: 'Length Ratio',
            latex: '\\frac{L_2}{L_1} = k',
            description: 'Corresponding lengths are in ratio k:1',
        },
        {
            id: 'similarity-area',
            name: 'Area Ratio',
            latex: '\\frac{A_2}{A_1} = k^2',
            description: 'Areas are in ratio k²:1',
        },
        {
            id: 'similarity-volume',
            name: 'Volume Ratio',
            latex: '\\frac{V_2}{V_1} = k^3',
            description: 'Volumes are in ratio k³:1',
        },
    ],
    derivatives: (state, params) => {
        const rate = getParam(params, 'rate');
        const scale = getState(state, 'scale');
        const nextScale = Math.max(0.5, Math.min(3, scale + rate * 0.016));
        const lengthRatio = nextScale;
        const areaRatio = nextScale * nextScale;
        const volumeRatio = nextScale * nextScale * nextScale;
        const perimeter1 = 12;
        const perimeter2 = perimeter1 * nextScale;
        return {
            scale: nextScale - scale,
            lengthRatio: lengthRatio - getState(state, 'lengthRatio'),
            areaRatio: areaRatio - getState(state, 'areaRatio'),
            volumeRatio: volumeRatio - getState(state, 'volumeRatio'),
            perimeter1: 0,
            perimeter2: perimeter2 - getState(state, 'perimeter2'),
        };
    },
    bounds: {
        scale: { min: 0.5, max: 3 },
        areaRatio: { min: 0.25, max: 9 },
        volumeRatio: { min: 0.125, max: 27 },
    },
});
// ============================================================================
// COORDINATE DISTANCE FORMULA
// ============================================================================
export const CoordinateDistanceFormula = createExperimentFromSchema({
    id: 'coordinate-distance-formula',
    name: 'Coordinate Distance Formula',
    domain: 'mathematics',
    description: 'Explore distance formula and midpoint in coordinate geometry',
    conceptTags: ['Distance Formula', 'Midpoint', 'Coordinates', 'Pythagorean Theorem'],
    difficulty: 2,
    stateVariables: [
        { id: 'x1', name: 'Point 1 X', unit: 'dimensionless', initial: 0, symbol: 'x_1' },
        { id: 'y1', name: 'Point 1 Y', unit: 'dimensionless', initial: 0, symbol: 'y_1' },
        { id: 'x2', name: 'Point 2 X', unit: 'dimensionless', initial: 3, symbol: 'x_2' },
        { id: 'y2', name: 'Point 2 Y', unit: 'dimensionless', initial: 4, symbol: 'y_2' },
        { id: 'distance', name: 'Distance d', unit: 'dimensionless', initial: 5, symbol: 'd' },
        { id: 'midX', name: 'Midpoint X', unit: 'dimensionless', initial: 1.5, symbol: 'm_x' },
        { id: 'midY', name: 'Midpoint Y', unit: 'dimensionless', initial: 2, symbol: 'm_y' },
        { id: 'dx', name: 'Δx', unit: 'dimensionless', initial: 3, symbol: '\\Delta x' },
        { id: 'dy', name: 'Δy', unit: 'dimensionless', initial: 4, symbol: '\\Delta y' },
    ],
    parameters: [
        { id: 'x2_target', name: 'Point 2 X', unit: 'dimensionless', min: -5, max: 5, default: 3, symbol: 'x_2' },
        { id: 'y2_target', name: 'Point 2 Y', unit: 'dimensionless', min: -5, max: 5, default: 4, symbol: 'y_2' },
        { id: 'angularSpeed', name: 'Angular Speed', unit: 'rad/s', min: -2, max: 2, default: 0.5, symbol: '\\omega' },
        { id: 'radius', name: 'Radius', unit: 'dimensionless', min: 1, max: 5, default: 3, symbol: 'r' },
    ],
    equations: [
        {
            id: 'distance-formula',
            name: 'Distance Formula',
            latex: 'd = \\sqrt{(x_2-x_1)^2 + (y_2-y_1)^2}',
            description: 'Distance between two points in coordinate plane',
        },
        {
            id: 'midpoint-formula',
            name: 'Midpoint Formula',
            latex: 'M = \\left(\\frac{x_1+x_2}{2}, \\frac{y_1+y_2}{2}\\right)',
            description: 'Coordinates of midpoint between two points',
        },
    ],
    derivatives: (state, params) => {
        const angularSpeed = getParam(params, 'angularSpeed');
        const radius = getParam(params, 'radius');
        const angle = getState(state, 'x2') * angularSpeed;
        const nextAngle = angle + angularSpeed * 0.016;
        const x1 = 0;
        const y1 = 0;
        const x2 = radius * Math.cos(nextAngle);
        const y2 = radius * Math.sin(nextAngle);
        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;
        return {
            x1: 0,
            y1: 0,
            x2: x2 - getState(state, 'x2'),
            y2: y2 - getState(state, 'y2'),
            distance: distance - getState(state, 'distance'),
            midX: midX - getState(state, 'midX'),
            midY: midY - getState(state, 'midY'),
            dx: dx - getState(state, 'dx'),
            dy: dy - getState(state, 'dy'),
        };
    },
    bounds: {
        distance: { min: 0, max: 15 },
        midX: { min: -5, max: 5 },
        midY: { min: -5, max: 5 },
    },
});
// ============================================================================
// LOCUS POINT INVESTIGATION
// ============================================================================
export const LocusPointInvestigation = createExperimentFromSchema({
    id: 'locus-point-investigation',
    name: 'Locus Point Investigation',
    domain: 'mathematics',
    description: 'Explore locus definitions and conic sections',
    conceptTags: ['Locus', 'Conic Sections', 'Ellipse', 'Parabola', 'Hyperbola', 'Focus', 'Directrix'],
    difficulty: 5,
    stateVariables: [
        { id: 't', name: 'Parameter t', unit: 'dimensionless', initial: 0, symbol: 't' },
        { id: 'x', name: 'X coordinate', unit: 'dimensionless', initial: 3, symbol: 'x' },
        { id: 'y', name: 'Y coordinate', unit: 'dimensionless', initial: 0, symbol: 'y' },
        { id: 'distFocus1', name: 'Distance to F1', unit: 'dimensionless', initial: 2, symbol: 'd_1' },
        { id: 'distFocus2', name: 'Distance to F2', unit: 'dimensionless', initial: 4, symbol: 'd_2' },
        { id: 'sumDist', name: 'Sum of Distances', unit: 'dimensionless', initial: 6, symbol: 'd_1+d_2' },
        { id: 'eccentricity', name: 'Eccentricity e', unit: 'dimensionless', initial: 0.67, symbol: 'e' },
    ],
    parameters: [
        { id: 'conicType', name: 'Conic Type', unit: 'dimensionless', min: 0, max: 2, default: 0, symbol: 'C' },
        { id: 'a', name: 'Semi-major axis a', unit: 'dimensionless', min: 2, max: 10, default: 3, symbol: 'a' },
        { id: 'b', name: 'Semi-minor axis b', unit: 'dimensionless', min: 1, max: 8, default: 2, symbol: 'b' },
        { id: 'c', name: 'Focal distance c', unit: 'dimensionless', min: 1, max: 6, default: 2, symbol: 'c' },
    ],
    equations: [
        {
            id: 'ellipse-locus',
            name: 'Ellipse Locus Definition',
            latex: '\\{(x,y): d_1 + d_2 = 2a\\}',
            description: 'Set of points where sum of distances to foci is constant',
        },
        {
            id: 'hyperbola-locus',
            name: 'Hyperbola Locus Definition',
            latex: '\\{(x,y): |d_1 - d_2| = 2a\\}',
            description: 'Set of points where difference of distances to foci is constant',
        },
        {
            id: 'parabola-locus',
            name: 'Parabola Locus Definition',
            latex: '\\{(x,y): d(P, F) = d(P, \\text{directrix})\\}',
            description: 'Set of points equidistant from focus and directrix',
        },
        {
            id: 'eccentricity',
            name: 'Eccentricity',
            latex: 'e = \\frac{c}{a}, \\quad \\begin{cases}e<1: \\text{ellipse} \\\\ e=1: \\text{parabola} \\\\ e>1: \\text{hyperbola}\\end{cases}',
            description: 'Eccentricity determines conic type',
        },
    ],
    derivatives: (state, params) => {
        const conicType = Math.round(getParam(params, 'conicType'));
        const a = getParam(params, 'a');
        const b = getParam(params, 'b');
        const c = getParam(params, 'c');
        const t = getState(state, 't');
        const nextT = t + 0.016;
        let x = 0;
        let y = 0;
        switch (conicType) {
            case 0: // Ellipse
                x = a * Math.cos(nextT);
                y = b * Math.sin(nextT);
                break;
            case 1: // Hyperbola
                x = a * Math.cosh(nextT % 3 - 1.5);
                y = b * Math.sinh(nextT % 3 - 1.5);
                break;
            case 2: // Parabola
                x = nextT % 4 - 2;
                y = x * x / (4 * c);
                break;
        }
        const distFocus1 = Math.sqrt((x - c) * (x - c) + y * y);
        const distFocus2 = Math.sqrt((x + c) * (x + c) + y * y);
        const sumDist = distFocus1 + distFocus2;
        const eccentricity = a > 0 ? c / a : 0;
        return {
            t: 0.016,
            x: x - getState(state, 'x'),
            y: y - getState(state, 'y'),
            distFocus1: distFocus1 - getState(state, 'distFocus1'),
            distFocus2: distFocus2 - getState(state, 'distFocus2'),
            sumDist: sumDist - getState(state, 'sumDist'),
            eccentricity: eccentricity - getState(state, 'eccentricity'),
        };
    },
    bounds: {
        x: { min: -10, max: 10 },
        y: { min: -10, max: 10 },
    },
});
// ============================================================================
// TRANSFORMATION COMPOSITION
// ============================================================================
export const TransformationComposition = createExperimentFromSchema({
    id: 'transformation-composition',
    name: 'Transformation Composition',
    domain: 'mathematics',
    description: 'Explore composition of geometric transformations',
    conceptTags: ['Transformations', 'Rotation', 'Reflection', 'Translation', 'Composition'],
    difficulty: 4,
    stateVariables: [
        { id: 'x', name: 'Original X', unit: 'dimensionless', initial: 1, symbol: 'x_0' },
        { id: 'y', name: 'Original Y', unit: 'dimensionless', initial: 0, symbol: 'y_0' },
        { id: 'tx', name: 'Transformed X', unit: 'dimensionless', initial: 1, symbol: 'x\'' },
        { id: 'ty', name: 'Transformed Y', unit: 'dimensionless', initial: 0, symbol: 'y\'' },
        { id: 'angle', name: 'Rotation Angle', unit: 'rad', initial: 0, symbol: '\\theta' },
    ],
    parameters: [
        { id: 'transform1', name: 'Transform 1', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'T_1' },
        { id: 'transform2', name: 'Transform 2', unit: 'dimensionless', min: 0, max: 3, default: 2, symbol: 'T_2' },
        { id: 'rotation', name: 'Rotation Angle', unit: 'rad', min: -Math.PI, max: Math.PI, default: 0, symbol: '\\theta' },
        { id: 'angleRate', name: 'Animation Rate', unit: 'rad/s', min: -1, max: 1, default: 0.3, symbol: '\\omega' },
    ],
    equations: [
        {
            id: 'rotation-matrix',
            name: 'Rotation Matrix',
            latex: '\\begin{bmatrix}x\' \\\\ y\'\\end{bmatrix} = \\begin{bmatrix}\\cos\\theta & -\\sin\\theta \\\\ \\sin\\theta & \\cos\\theta\\end{bmatrix}\\begin{bmatrix}x \\\\ y\\end{bmatrix}',
            description: 'Rotation by angle θ about origin',
        },
        {
            id: 'reflection-matrix',
            name: 'Reflection Matrix',
            latex: 'R_x = \\begin{bmatrix}1 & 0 \\\\ 0 & -1\\end{bmatrix}, \\quad R_y = \\begin{bmatrix}-1 & 0 \\\\ 0 & 1\\end{bmatrix}',
            description: 'Reflection across x-axis or y-axis',
        },
        {
            id: 'composition',
            name: 'Transformation Composition',
            latex: '(T_2 \\circ T_1)(P) = T_2(T_1(P))',
            description: 'Apply T1 first, then T2',
        },
    ],
    derivatives: (state, params) => {
        const transform1 = Math.round(getParam(params, 'transform1'));
        const transform2 = Math.round(getParam(params, 'transform2'));
        const baseRotation = getParam(params, 'rotation');
        const angleRate = getParam(params, 'angleRate');
        const angle = getState(state, 'angle');
        const nextAngle = angle + angleRate * 0.016;
        const x = 1;
        const y = 0;
        // Apply first transformation
        let x1 = x;
        let y1 = y;
        switch (transform1) {
            case 0: // Identity
                break;
            case 1: // Rotate
                x1 = x * Math.cos(baseRotation) - y * Math.sin(baseRotation);
                y1 = x * Math.sin(baseRotation) + y * Math.cos(baseRotation);
                break;
            case 2: // Reflect across x-axis
                y1 = -y;
                break;
            case 3: // Reflect across y-axis
                x1 = -x;
                break;
        }
        // Apply second transformation
        let tx = x1;
        let ty = y1;
        switch (transform2) {
            case 0: // Identity
                break;
            case 1: // Rotate
                tx = x1 * Math.cos(nextAngle) - y1 * Math.sin(nextAngle);
                ty = x1 * Math.sin(nextAngle) + y1 * Math.cos(nextAngle);
                break;
            case 2: // Reflect across x-axis
                ty = -y1;
                break;
            case 3: // Reflect across y-axis
                tx = -x1;
                break;
        }
        return {
            x: 0,
            y: 0,
            tx: tx - getState(state, 'tx'),
            ty: ty - getState(state, 'ty'),
            angle: nextAngle - angle,
        };
    },
    bounds: {
        tx: { min: -2, max: 2 },
        ty: { min: -2, max: 2 },
    },
});
//# sourceMappingURL=geometry.js.map